import { ArrowPathIcon, ChevronDownIcon, ClockIcon, PlayIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { AiSchedule, getAiProjectSchedules, runAiScheduleNow } from 'services'
import { Button } from 'stories/components'
import { getActiveTimeZone } from 'utils/timezone'

import { ScheduleRunsModal } from './ScheduleRunsModal'

/** Breathing room between the menu and the edge of whatever clips it. */
const MENU_GUTTER = 8
/** Below this the menu is useless anyway; better to clip than to render a sliver. */
const MENU_MIN_WIDTH = 200

/**
 * What "run this project" asks for.
 *
 * A project's instructions and knowledge are already in the system prompt for a project turn, so
 * this only has to say "now, and on this file". Restating the playbook here would compete with it.
 */
export const runProjectPrompt = (loanNumber?: number) =>
  loanNumber
    ? `Run this project on loan ${loanNumber} now: carry out its instructions against this loan and report what you did.`
    : 'Run this project now: carry out its instructions and report what you did.'

/**
 * The schedule's own zone, named only when it is not the viewer's - the same rule the schedules
 * panel follows, so a Chicago schedule does not read as a Pacific one here either.
 */
const zoneSuffix = (timezone?: string) => {
  if (!timezone || timezone === getActiveTimeZone()) return ''
  return ` ${String(timezone).split('/').pop()?.replace(/_/g, ' ')} time`
}

interface RunProjectButtonProps {
  projectId: number
  /**
   * Set inside a loan: the menu lists that loan's runs plus the project's unscoped ones, and a run
   * fired from here goes against this loan rather than the schedule's own saved scope.
   */
  loanNumber?: number
  /** 'compact' for the assistant's context bar; 'button' for a page header. */
  variant?: 'button' | 'compact'
  /** Jump to the chat a run wrote into. Only a screen with a conversation rail can do this. */
  onOpenConversation?: (conversationId: number) => void
  /** Told after a run starts, so a schedules list already on screen can refresh its status. */
  onStarted?: () => void
  /**
   * Run the project itself, right now, in the chat on screen - what pressing Run does when the
   * host has a conversation to run it in. Handed the prompt rather than composing its own, so the
   * assistant panel and the project header ask for the same thing.
   *
   * A host with no chat leaves this unset and the control stays what it was: the saved runs alone.
   */
  onRunProject?: (prompt: string) => void | Promise<void>
  /**
   * True while the host's chat is mid-turn. The control goes busy with it, so Run cannot start a
   * second agent in a thread that already has one going - and it stays the same shape while it does,
   * rather than losing the saved-runs chevron for the length of a turn.
   */
  isHostBusy?: boolean
}

/**
 * Run this project now - the project itself, or one of its saved runs.
 *
 * Pressing Run runs the PROJECT: a turn in the chat on screen that carries out the project's own
 * instructions against the loan in front of you. That is what people mean by it, and it is the
 * whole of what a project is - instructions and knowledge - so nothing else has to be set up first.
 * It happens in the chat rather than in the background because the point of pressing it by hand is
 * watching it work.
 *
 * The chevron opens the project's SAVED runs: schedules, which add a prompt of their own and a
 * cadence, fired off-cadence through the same `POST /ai/schedules/:id/run` the schedules panel
 * uses. A paused schedule is listed and runnable - switching the cadence off is not a reason you
 * cannot ask for the thing by hand, and the server allows it to anyone who can read the project.
 *
 * Either way, inside a loan the run is pinned to that loan. The project in the dropdown is what you
 * asked to run and the file on screen is what you are looking at, so a whole-project schedule fired
 * here is one run on this loan - not a fan-out over its filters, and not a run with no loan at all,
 * which is what the schedule's own scope would otherwise have given.
 */
export function RunProjectButton({
  projectId,
  loanNumber,
  variant = 'button',
  onOpenConversation,
  onStarted,
  onRunProject,
  isHostBusy = false,
}: RunProjectButtonProps) {
  const [open, setOpen] = useState(false)
  const [isRunningProject, setRunningProject] = useState(false)
  const [schedules, setSchedules] = useState<AiSchedule[] | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [startingId, setStartingId] = useState<number | null>(null)
  const [watching, setWatching] = useState<AiSchedule | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    try {
      const data = await getAiProjectSchedules(projectId, loanNumber)
      setSchedules(data || [])
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [projectId, loanNumber])

  // Read on every open rather than once: a schedule added in the panel next door, or by someone
  // else, should be in the menu the next time it is pulled down.
  useEffect(() => {
    if (open) load()
  }, [open, load])

  // Switching project in the assistant leaves a menu listing the previous project's runs.
  useEffect(() => {
    setOpen(false)
    setSchedules(null)
  }, [projectId])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const onRun = async (schedule: AiSchedule) => {
    setStartingId(schedule.id)
    try {
      await runAiScheduleNow(schedule.id, loanNumber)
      setOpen(false)
      // The answer is not back - the run row is, and an agentic turn can take minutes. The runs
      // view polls from here, which is the only place a failed run ever shows.
      setWatching(schedule)
      toast(`Started "${schedule.name}"${loanNumber ? ` on loan ${loanNumber}` : ''}.`, { type: 'success' })
      onStarted?.()
    } catch {
      // Surfaced by the global API error handler.
    } finally {
      setStartingId(null)
    }
  }

  /**
   * Run the project itself. The host owns the turn - it has the thread, the live steps and the
   * stop button - so this only hands over the prompt and keeps the control busy while it lasts.
   */
  const onRunProjectNow = async () => {
    if (!onRunProject) return
    setOpen(false)
    setRunningProject(true)
    try {
      await onRunProject(runProjectPrompt(loanNumber))
    } finally {
      setRunningProject(false)
    }
  }

  const canRunProject = !!onRunProject
  const isBusy = startingId !== null || isRunningProject || isHostBusy

  // The menu hangs from the edge the trigger sits on. In the assistant the control is at the right
  // of a narrow panel, so right-anchoring keeps it inside; in the page header the button group wraps
  // to the LEFT of a wide page, where a right-anchored menu runs off the viewport entirely.
  const align = variant === 'compact' ? 'right-0' : 'left-0'

  /**
   * Pull the menu back inside whatever clips it. Compact only - the page-header variant opens into
   * an empty page and needs none of this.
   *
   * Anchoring to the trigger is not enough in the assistant: the control sits partway along a
   * narrow `overflow-hidden` column, so a menu hung from its right edge pokes out of the column's
   * LEFT and every line is sliced in half. The panel is resizable, so no fixed offset works - the
   * clipping edge has to be measured each time the menu opens.
   *
   * `position: fixed` is not the escape it looks like: the assistant's own <aside> carries a
   * transform, which makes it the containing block for fixed descendants, so the column goes on
   * clipping them. A portal would escape, but it would also put the menu outside `containerRef`
   * and break the click-outside handler below - see the nested-dialog trouble portals caused here
   * before. Measuring is the smaller change.
   */
  const fitMenuInsideClip = useCallback(() => {
    const menu = menuRef.current
    if (!menu || variant !== 'compact') return
    menu.style.right = ''
    menu.style.maxWidth = ''

    let clip = menu.parentElement
    while (clip && clip !== document.documentElement && getComputedStyle(clip).overflowX === 'visible') {
      clip = clip.parentElement
    }
    if (!clip || clip === document.documentElement) return

    const bounds = clip.getBoundingClientRect()
    menu.style.maxWidth = `${Math.max(MENU_MIN_WIDTH, bounds.width - MENU_GUTTER * 2)}px`

    // Measured after the width clamp, since clamping moves the right-anchored left edge.
    const box = menu.getBoundingClientRect()
    const pokesOut = Math.max(0, bounds.left + MENU_GUTTER - box.left)
    const roomToMove = Math.max(0, bounds.right - MENU_GUTTER - box.right)
    const shift = Math.min(pokesOut, roomToMove)
    if (shift > 0) menu.style.right = `${-shift}px`
  }, [variant])

  // Before paint, so the menu never shows in the clipped position first. Re-run when the schedules
  // land: the empty state and a loaded list are different heights, and a resize can land between.
  useLayoutEffect(() => {
    if (!open) return
    fitMenuInsideClip()
    window.addEventListener('resize', fitMenuInsideClip)
    return () => window.removeEventListener('resize', fitMenuInsideClip)
  }, [open, schedules, fitMenuInsideClip])

  // Where a click sends the work, which inside a loan is this loan whatever the schedule stored.
  // Repeating the saved scope there would name a target the run no longer has.
  const scopeLabel = (schedule: AiSchedule) => {
    if (loanNumber) return `Runs on loan ${loanNumber}`
    if (schedule.loanNumber) return `Loan ${schedule.loanNumber}`
    return schedule.loanFilters ? 'A set of loans' : 'Whole project'
  }

  const triggerTitle = canRunProject
    ? loanNumber
      ? `Run this project on loan ${loanNumber} now`
      : 'Run this project now'
    : loanNumber
      ? `Run one of this project's saved runs on loan ${loanNumber}`
      : "Run one of this project's saved runs now"

  const savedRunsTitle = loanNumber ? `This project's saved runs, on loan ${loanNumber}` : "This project's saved runs"

  // Pressing Run runs the project; the chevron is what opens the saved runs. With no way to run the
  // project here, the whole control is the menu it always was.
  const onTriggerClick = canRunProject ? onRunProjectNow : () => setOpen((prev) => !prev)

  return (
    <div className="relative shrink-0" ref={containerRef}>
      {variant === 'compact' ? (
        <span className="shrink-0 flex items-center">
          <button
            className="shrink-0 flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold text-gray-600 hover:bg-white hover:text-shade-blue disabled:opacity-50"
            disabled={isBusy}
            title={triggerTitle}
            onClick={onTriggerClick}
          >
            {isBusy ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
            Run
          </button>
          {canRunProject && (
            <button
              className="shrink-0 rounded px-0.5 py-1 text-gray-500 hover:bg-white hover:text-shade-blue disabled:opacity-50"
              disabled={isBusy}
              title={savedRunsTitle}
              aria-label={savedRunsTitle}
              onClick={() => setOpen((prev) => !prev)}
            >
              <ChevronDownIcon className="h-3 w-3" />
            </button>
          )}
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <Button size="sm" color="gray" disabled={isBusy} title={triggerTitle} onClick={onTriggerClick}>
            <span className="flex items-center gap-1.5">
              {isBusy ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
              Run project
            </span>
          </Button>
          {canRunProject && (
            <Button
              size="sm"
              color="gray"
              // Narrower than the Button's own px-5, which on a lone chevron reads as a second
              // full-width button. `!` beats it outright - class order in the attribute does not.
              className="!px-2"
              disabled={isBusy}
              title={savedRunsTitle}
              aria-label={savedRunsTitle}
              onClick={() => setOpen((prev) => !prev)}
            >
              <ChevronDownIcon className="w-4 h-4" />
            </Button>
          )}
        </span>
      )}

      {open && (
        <div
          ref={menuRef}
          className={`absolute ${align} top-full z-20 mt-1 w-72 sm:w-80 rounded-md border border-gray-200 bg-white shadow-lg`}
        >
          <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
            {canRunProject
              ? loanNumber
                ? `Saved runs, on loan ${loanNumber}`
                : 'Saved runs'
              : loanNumber
                ? `Run now on loan ${loanNumber}`
                : 'Run now'}
          </p>

          {isLoading && !schedules && (
            <p className="px-3 py-3 text-sm text-gray-500 flex items-center gap-1.5">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Loading this project&apos;s runs…
            </p>
          )}

          {failed && <p className="px-3 py-3 text-sm text-gray-500">Could not load this project&apos;s runs.</p>}

          {!!schedules && !schedules.length && !failed && (
            <p className="px-3 py-3 text-sm text-gray-500">
              {canRunProject
                ? "No saved runs yet. Run already carries out this project's own instructions - a schedule is for a run with a prompt of its own, added under Schedules in the project workspace, and it can be fired from here whether or not its cadence is on."
                : "Nothing saved to run yet. A project's runs are its schedules - add one under Schedules in the project workspace, and it can be fired from here whether or not its cadence is on."}
            </p>
          )}

          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
            {(schedules || []).map((schedule) => (
              <button
                key={schedule.id}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                disabled={isBusy}
                onClick={() => onRun(schedule)}
              >
                <span className="flex items-center gap-1.5">
                  {startingId === schedule.id ? (
                    <ArrowPathIcon className="w-3.5 h-3.5 shrink-0 animate-spin text-shade-blue" />
                  ) : (
                    <PlayIcon className="w-3.5 h-3.5 shrink-0 text-shade-blue" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{schedule.name}</span>
                  {!schedule.isEnabled && <span className="shrink-0 text-[11px] text-gray-400">Paused</span>}
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
                  <ClockIcon className="w-3 h-3 shrink-0" />
                  <span className="truncate">
                    {schedule.description || schedule.frequency}
                    {zoneSuffix(schedule.timezone)}
                  </span>
                </span>
                <span className="block text-[11px] text-gray-400">{scopeLabel(schedule)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {watching && (
        <ScheduleRunsModal
          schedule={watching}
          onOpenConversation={onOpenConversation}
          onClose={() => {
            setWatching(null)
            // A finished run leaves a new chat and a new last-run status behind it.
            onStarted?.()
          }}
        />
      )}
    </div>
  )
}
